import { Injectable } from '@angular/core';
import moment from 'moment';
import * as PdfJs from 'pdfjs-dist';
import { PDFDocument, StandardFonts, rgb, scale, degrees } from 'pdf-lib';
import { saveAs as downloadFile } from "file-saver";
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Sign } from '../_models/sign';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})

export class PdfService {
  constPDFLimit: Number = 9999;
  constructor(
    private toastr: ToastrService,
    private translateService: TranslateService,
    private http: HttpClient
  ) { }

  private searchPageCoor(doc, pageNumber, fields, founds) {
    return doc.getPage(pageNumber).then(page => {
      return page.getTextContent();
    }).then(content => {
      let a = {};
      fields.forEach(k => {
        let lang = '';
        let searchWords = k.Text?.split(';') || [];
        let founded = null;

        if (founds[k.Name] == null)
          founds[k.Name] = [];

        searchWords.forEach(w => {
          if (!w || a[k.Name]) return;

          let formatTxt = w.split("^");
          lang = formatTxt.pop();
          const text = formatTxt.join("^");

          founded = content.items.filter(i => i.str.toLowerCase().includes(text.toLowerCase()));

          if (founded.length > 0 && (founds[k.Name].length == 0 || !founds[k.Name].includes(text))) {
            founded = k.Order && founded.length > 1 && !!founded[k.Order - 1] ? founded[k.Order - 1] : founded[0];
            a[k.Name] = { ...founded, lang };
            founds[k.Name].push(text);
          }
        });
      });
      return { page: pageNumber, items: a };
    });
  }

  groupTextByLines(items, yThreshold = 2) {
    const lines = [];

    items.forEach(item => {
      const y = item.transform[5];
      let line = lines.find(l => Math.abs(l.y - y) < yThreshold);
      if (!line) {
        line = { y, textItems: [] };
        lines.push(line);
      }
      line.textItems.push(item);
    });

    // Ordenar horizontalmente y concatenar texto
    lines.forEach(line => {
      line.textItems.sort((a, b) => a.transform[4] - b.transform[4]);
      line.lineText = line.textItems.map(t => t.str).join('').replace(/\s+/g, ' ').trim();
    });

    return lines;
  }


  private searchPageCoorFront(doc, pageNumber, fields) {
    return doc.getPage(pageNumber).then(page => {
      return page.getTextContent();
    }).then(content => {
      const lines = this.groupTextByLines(content.items);
      let a = {};

      fields.forEach(k => {
        const searchWords = k.Text?.split(';') || [];
        let foundItem = null;
        let lang = '';

        for (const w of searchWords) {
          if (!w || a[k.Name]) break;

          const formatTxt = w.split('^');
          lang = formatTxt.pop();
          const searchText = formatTxt.join('^').toLowerCase();

          for (const line of lines) {
            if (line.lineText.toLowerCase().includes(searchText)) {
              foundItem = line.textItems.find(i => i.str.toLowerCase().includes(searchText.split(' ')[0]));
              break;
            }
          }

          if (foundItem) {
            a[k.Name] = { ...foundItem, lang };
            break;
          }
        }
      });

      return { page: pageNumber, items: a };
    });
  }



  print(pdfs: Array<string>): void {
    if (pdfs.length) {
      pdfs.forEach(p => {
        let oWindow = window.open(p, "print");
        oWindow.print();
        oWindow.close();
      });
    }
  }

  async searchCoordinates(documents) {
    let results = [];

    for (const d of documents) {
      console.log(d);
      results.push(await this.searchCoordinatesDocumentAsync(d));
    }

    return Promise.all(results);
  }

  async searchCoordinatesDocumentAsync(document) {

    const pdfData = document.pdf instanceof ArrayBuffer
      ? document.pdf.slice(0)
      : new Uint8Array(document.pdf).slice();
    let loading = PdfJs.getDocument(pdfData);

    return loading.promise.then(async doc => {
      let results = [];
      var founds = {};
      if (document.SignType && !document.SignType.BackendSign) {
        for (let i = 1; i <= doc.numPages; i++)
          results.push(await this.searchPageCoorFront(doc, i, document.SignType.Fields));
      } else {
        for (let i = 1; i <= doc.numPages; i++)
          results.push(await this.searchPageCoor(doc, i, document.SignType.Fields, founds));
      }
      return Promise.all(results);
    }).then(searchResults => {
      document.coord = searchResults;
    }, err => {
      console.error(err);
      this.toastr.error(this.translateService.instant('sign.pdf_get_error'));
      throw new Error(err);
    });
  }


  getPdfBlobForPrinting(pdfs: Array<Object>) {
    return new Promise(async (resolve, reject) => {
      if (pdfs && pdfs.length) {
        let mergedPdf = await PDFDocument.create();
        let copiedPages;
        for (const p of pdfs) {
          const newPdf = await PDFDocument.load(p['pdf'], { ignoreEncryption: true });
          copiedPages = await mergedPdf.copyPages(newPdf, newPdf.getPageIndices());
          copiedPages.forEach((page) => mergedPdf.addPage(page));
        }
        const mergedPdfFile = await mergedPdf.save({ updateFieldAppearances: false });
        let blob = new Blob([mergedPdfFile], { type: "application/pdf" });
        resolve(blob);
      }
    });
  }

  downloadPdfs(pdfs: Array<Object>) {
    try {
      pdfs.forEach(p => {
        const blob = new Blob([p['pdf']], { type: "application/pdf" });
        downloadFile(blob, p['downloadName']);
      });
      this.toastr.success(this.translateService.instant('search.download_request_success'), '', { closeButton: true, timeOut: 6000, progressBar: true, enableHtml: true });
    } catch (e) {
      this.toastr.error(this.translateService.instant('search.download_request_error'), '', { closeButton: true, timeOut: 6000, progressBar: true, enableHtml: true });
    }
  }

  arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  cleanDocuments(documents: any): any[] {
    const cleanedDocuments = documents.map(doc => {
      const { Id, AdditionData, DocumentDate, DocumentTime, DocumentType, OrderItem, Order, ServiceRequest, ServiceRequestItem, Tally, Trip, ExternalID, OD, ODItems, Signers, CarrierCode, CarrierDescription, TransportUnit, Trailer, DriverName, CompanyCode, PlantOfOd, Customer, CustomerDescription, Destination, DestinationDescription, Signed, SourceDocs, TargetDocs, LastUser, LastUpdateDate, AdditionalData, SPSiteURI, DocumentName, DocumentURI, DocumentSignedUri, DocumentFolder, DocumentLibraryName, Language, Yard, SignType, MaterialDescription, Nominated, StatusId, Status, isMtc, isDatabook, ExceededLimit, DocumentPdf, Checked, Select, pdf, downloadName, docId, coord } = doc;
      const base64Pdf = this.arrayBufferToBase64(pdf);
      return { Id: docId, AdditionData, DocumentDate, DocumentTime, DocumentType, OrderItem, Order, ServiceRequest, ServiceRequestItem, Tally, Trip, ExternalID, OD, ODItems, Signers: Signers ?? [], Coord: coord, CarrierCode, CarrierDescription, TransportUnit, Trailer, DriverName, CompanyCode, PlantOfOd, Customer, CustomerDescription, Destination, DestinationDescription, Signed, SourceDocs, TargetDocs, LastUser, LastUpdateDate, AdditionalData, SPSiteURI, DocumentName, DocumentURI, DocumentSignedUri, DocumentFolder, DocumentLibraryName, Language, Yard, SignType, MaterialDescription, Nominated, StatusId, Status, isMtc, isDatabook, ExceededLimit, DocumentPdf: { contentType: null, fileName: downloadName, file: base64Pdf }, Checked, Select };
    });
    return cleanedDocuments;
  }

  async stampSignatures(pdfs, signList: Array<Sign>, isNominated: boolean, signAll: boolean, isDriverView: boolean): Promise<any> {
    if (pdfs.length == 0) {
      this.toastr.error(this.translateService.instant('sign.no_documents_error'));
      return;
    }

    var backendPdfs = pdfs.filter(p => p.SignType && p.SignType.BackendSign);
    var frontendPdfs = pdfs.filter(p => p.SignType && !p.SignType.BackendSign);
    var response = [];
    const promises = [];

    if (backendPdfs.length > 0) {
      promises.push(
        this.stampSignaturesInBackend(backendPdfs, signList, isNominated, signAll, isDriverView).then(backendSignedPdfs => {
          response.push(...backendSignedPdfs); // Agregar los resultados al arreglo response
        })
      );
    }

    if (frontendPdfs.length > 0) {
      promises.push(
        this.stampSignaturesInFrontend(frontendPdfs, signList, isNominated, signAll, isDriverView).then(frontendSignedPdfs => {
          response.push(...frontendSignedPdfs); // Agregar los resultados al arreglo response
        })
      );
    }

    await Promise.all(promises);
    return response;
  }

  async stampSignaturesInBackend(pdfs, signList: Array<Sign>, isNominated: boolean, signAll: boolean, isDriverView: boolean) {
    var locationId = pdfs.length > 0 ? pdfs[0].SignType.LocationId : 1;
    var documentIds = pdfs.map(item => { return item.docId; });
    var timeZoneInfo = Intl.DateTimeFormat().resolvedOptions().timeZone;
    var culture = Intl.DateTimeFormat().resolvedOptions().locale;
    var body = { documentIds, signList, isNominated, signAll, isDriverView, locationId, timeZoneInfo, culture };
    return this.http.post<any>(`${environment.apiUrl}/api/documentdetail/stampSignatures`, body).toPromise().then(response => {
      return response.map(item => {
        const binaryString = atob(item.pdf);
        const byteArray = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          byteArray[i] = binaryString.charCodeAt(i);
        }
        return {
          docId: item.docId,
          pdf: byteArray.buffer
        };
      });
    });
  }

  async stampSignaturesInFrontend(pdfs, signList: Array<Sign>, isNominated, signAll, isDriverView) {
    let signedPdfs = [];
    try {
      //loop docs
      for (let doc of pdfs) {

        //load doc pdf
        let pdfDoc = await PDFDocument.load(doc.pdf);

        if (isNominated) {
          const signed = doc.Signers.filter(d => d.Status.toLowerCase() === 'signed');
          if (signed.length < 4) {
            doc.SignType.Fields = doc.SignType.Fields.filter(f => f.Name.includes(signed.length + 1));

            //filtro subtype en signtype si existe (Aplicamos esto cuando corrijamos la nomenclatura?)
            // if(currentSignSubtype.length > 0)

            //filtro subtype por stamp

            var currentSignSubtype = signList[0].subType;

            if (currentSignSubtype.includes('stamp'))
              doc.SignType.Fields = doc.SignType.Fields.filter(f => f.Name.toLowerCase().includes(currentSignSubtype.toLowerCase()));
            else
              doc.SignType.Fields = doc.SignType.Fields.filter(f => !f.Name.toLowerCase().includes('stamp'));
          }
          else {
            doc.SignType.Fields = [];
          }
        }

        var signListImgIndex = 0;
        for (const f of doc.SignType.Fields) {
          for (const co of doc.coord) {

            // const page = pages[co.page - 1];
            const pdfPages = pdfDoc.getPages();
            let pages = [];
            let page = [];

            pdfPages.forEach((value, index) => {
              if (index == co.page - 1) {
                page.push(value);
              }
              else {
                pages.push(value)
              }
            });

            if ((!signAll && f.ApplyAllPage) || (f.ApplyAllPage && !!co.items[f.Name])) continue;
            if ((!!!co.items[f.Name] || (!!!co.items[f.Name] && co.page < doc.coord[doc.coord.length - 1].page)) && !f.ApplyAllPage) continue;

            let pos_x = f.PositionX ? f.PositionX : 0;
            let pos_y = f.PositionY ? f.PositionY : 0;
            let delta_x = f.DeltaX ? f.DeltaX : 0;
            let delta_y = f.DeltaY ? f.DeltaY : 0;
            let lang = doc.Language ? doc.Language : 'en';

            if (!!co.items[f.Name] && !f.Name.includes('allpages')) {
              pos_x = co.items[f.Name].transform[4] + (doc.SignType.DocumentType === 'MTC' ? 100 : 0);
              pos_y = co.items[f.Name].transform[5];
              lang = co.items[f.Name].lang ? co.items[f.Name].lang : lang;
            }

            switch (f.Type) {
              case 'img':
                await this.insertImg(pdfDoc, page, pos_x, pos_y, signList[signListImgIndex], delta_x, delta_y, f.Scale, f.Transparency);
                if (signList.length > 1 && signListImgIndex < (signList.length - 1)) {
                  signListImgIndex++;
                }
                break;
              case 'stamp':
                await this.insertImg(pdfDoc, page, pos_x, pos_y, signList.find(x => x.type == "stamp"), delta_x, delta_y, f.Scale, f.Transparency);
                break;
              case 'text':
                await this.insertText(pdfDoc, page, pos_x, pos_y, signList[0].printName, delta_x, delta_y, true, doc.SignType.Size, f.Transparency);
                break;
              case 'date':
                let date = moment().locale(lang).format('DD-MMM-YYYY HH:mm').toUpperCase();
                await this.insertText(pdfDoc, page, pos_x, pos_y, date, delta_x, delta_y, false, doc.SignType.Size, f.Transparency);
                break;
            }
          }
        }
        //update pdf
        const pdfBytes = await pdfDoc.save();
        signedPdfs.push({
          docId: doc.docId,
          pdf: pdfBytes
        });
      }
      return signedPdfs;
    }
    catch (err) {
      throw new Error(err);
    }
  }

  private async insertImg(pdfDoc, pages, pos_x, pos_y, signItem, delta_x = 0, delta_y = 0, scale = 0.50, transparency = 1) {
    const ImageBytes = await this.convertDataURIToBinary(signItem.sign);
    var Image;
    if (signItem.contentType.includes("png"))
      Image = await pdfDoc.embedPng(ImageBytes);
    if (signItem.contentType.includes("jpg") || signItem.contentType.includes("jpeg"))
      Image = await pdfDoc.embedJpg(ImageBytes);
    const Dims = await Image.scale(scale);
    const maxWidth: number = 180;
    const maxHeight: number = 50;
    Dims.width = Dims.width / 1.5;
    Dims.height = Dims.height / 2.5;
    if(Dims.width > maxWidth) Dims.width = maxWidth;
    if(Dims.height > maxHeight) Dims.height = maxHeight;
    const image = async () => {
      for (const page of pages) {
        const rotation = page.getRotation().angle;
        var context = { pos_x, pos_y, delta_x, delta_y };
        this.adjustPosition(page, rotation, context, Dims.height, Dims.width);
        page.drawImage(Image, {
          x: context.pos_x + context.delta_x,
          y: context.pos_y + context.delta_y,
          width: Dims.width,
          height: Dims.height,
          opacity: transparency,
          rotate: degrees(rotation)
        });
      }
    }
    await image();
  }

  private async insertText(pdfDoc, pages, pos_x, pos_y, txt, delta_x = 0, delta_y = 0, dynamicTxt = false, size = 8.5, transparency = 1) {
    if (txt == null) return;
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
    if (dynamicTxt && txt.length > 10)
      delta_x -= helveticaFont.widthOfTextAtSize(txt, txt.length) / (txt.length / 1.8);

    var width = helveticaFont.widthOfTextAtSize(txt, size) / 1.5;
    var height = helveticaFont.heightAtSize(size) / 2.5;
    for (const page of pages) {
      const rotation = page.getRotation().angle;
      var context = { pos_x, pos_y, delta_x, delta_y };
      this.adjustPosition(page, rotation, context, height, width);
      page.drawText(txt, {
        x: context.pos_x + context.delta_x,
        y: context.pos_y + context.delta_y,
        size: size,
        font: helveticaFont,
        color: rgb(0, 0, 0),
        opacity: transparency,
        rotate: degrees(rotation)
      });
    }
  }

  private adjustPosition(page, rotation: number, context: any, height: number, width: number) {

    switch (rotation) {
      case 0:
        if (context.pos_x == this.constPDFLimit) {
          context.pos_x = page.getWidth();
          context.pos_x -= width;
        }
        if (context.pos_y == this.constPDFLimit) {
          context.pos_y = page.getHeight();
          context.pos_y -= height;
        }
        break;
      case 90:
        if (context.pos_x == this.constPDFLimit) {
          context.pos_x = page.getWidth();
        }
        if (context.pos_y > page.getHeight()) {
          context.pos_y = 0;
        }
        else {
          context.pos_y = page.getHeight() - context.pos_y;
          context.pos_y -= width;
        }
        var aux_delta = context.delta_x;
        context.delta_x = 0 - context.delta_y;
        context.delta_y = aux_delta;
        break;
    }

  }

  private convertDataURIToBinary(dataURI) {
    var BASE64_MARKER = ';base64,';
    var base64Index = dataURI.indexOf(BASE64_MARKER) + BASE64_MARKER.length;
    var base64 = dataURI.substring(base64Index);
    var raw = window.atob(base64);
    var rawLength = raw.length;
    var array = new Uint8Array(new ArrayBuffer(rawLength));

    for (let i = 0; i < rawLength; i++) {
      array[i] = raw.charCodeAt(i);
    }
    return array;
  }
}
